import type { Timestamp } from "firebase/firestore";

/** docs/dashboard-design.md §3 のデータモデル */

export type Role = "admin" | "chef" | "supervisor" | "worker";

export interface UserDoc {
  displayName: string;
  email: string;
  role: Role;
  /** 上長(2026-08-08 決定2)。評価結果の閲覧範囲に使う */
  supervisorUid: string | null;
  /** 担当品目(決定1)。作業者はここにある品目しか見られない */
  assignedDishIds: string[];
}

export type DishStatus = "draft" | "reviewing" | "approved";

export interface DishDoc {
  name: string;
  nameJa: string | null;
  /**
   * 料理名の中国語訳(マニュアル本文の見出しと同じ訳)。ワーカーが組版のたびに
   * analysis.translations.zh から書く。未翻訳なら無い(日本語名で出す)。改名しても
   * 次の組版までは旧名の訳のまま(マニュアル本文の見出しと同じ扱い)。
   */
  nameZh?: string | null;
  status: DishStatus;
  version: number;
  isGolden: boolean;
  createdBy: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  /** 正の analysis.json(gs:// URI)。生成前は null */
  currentAnalysisPath: string | null;
  /** ルールの逆引き用。users.assignedDishIds と対で保つ */
  assigneeUids: string[];
  /**
   * 生成済みマニュアルの言語(例 ["ja","zh"])。render のたびにワーカーが実ファイルから書く。
   * 無い品目(再 render 前)は日本語のみとして扱う。翻訳していない品目で中国語タブを
   * 出すと「表示できませんでした」になり、権限の問題と紛らわしいため。
   */
  docLangs?: string[];
  /** ①でアップロードされた素材 */
  sourceVideoPath: string | null;
  /**
   * 分割録画のパート（結合順）。これが正で、sourceVideoPath は後方互換の1本目。
   * 古い品目には無いので、読むときは materialsOf() を通す。
   */
  sourceVideoPaths?: string[];
  recipeDocPaths: string[];
  /** 補足メモ（仕込み済みの前提など）。analyze の --hints に渡る */
  hints?: string | null;
  /**
   * 素材を最後に差し替えた時刻。直近の analyze 完了より後なら、
   * 出ているマニュアルは古い素材のものになる（画面で注意を出す）。
   */
  materialsUpdatedAt?: Timestamp | null;
  /**
   * 採点のカテゴリの重み(百分率・合計100)。無ければ既定(lib/score-weights.ts)。
   * 書き込みは Callable(setScoreWeights)のみ(台帳に残すため)
   */
  scoreWeights?: Partial<Record<"critical_point" | "ingredient" | "heat_level" | "plating", number>> | null;
  /** レビューの進み具合。一覧に進捗率を出すための非正規化（無い品目＝未計測） */
  review?: ReviewProgress | null;
  /** 実行中のジョブ。無ければ null（jobs のトリガが書く） */
  activeJob?: ActiveJob | null;
  /** 直近の実行が失敗していればその記録。次の実行が始まると消える */
  failedJob?: FailedJob | null;
  /**
   * 削除した時刻(論理削除。functions/delete-dish.mjs)。入っていれば一覧・評価の選択肢から外す。
   * 素材・マニュアル・評価の記録は残り、管理者が品目画面から元に戻せる
   */
  deletedAt?: Timestamp | null;
  deletedBy?: string | null;
  deletedByName?: string | null;
  deleteReason?: string | null;
}

/**
 * レビューの進み具合（品目一覧の進捗表示）。
 *
 * 分母 open は「いま残っている確認事項」で、再組版のたびにワーカーが
 * review_questions.json から数え直す（回答が本文へ反映されると質問は消えるので、
 * 分母は生成のたびに減る）。分子 answered / held は回答のトリガが数える。
 *
 * **どちらも数えているだけで、質問そのものは CLI が正。**
 */
export interface ReviewProgress {
  /** いま残っている確認事項の数 */
  open: number;
  /**
   * その確認事項の id。回答との突き合わせに使う（トリガが読む）。
   *
   * 数だけでは「反映済みの古い回答」と「今の質問への回答」を区別できず、
   * 反映のたびに進捗率が実態とずれる。id は CLI 出力の写しで、
   * 画面はこれを表示にも抽出にも使わない（実測47問・約1KB）。
   */
  openIds: string[];
  /** open のうち回答が入っている数 */
  answered: number;
  /** これまでに解決した確認事項の累計(#200)。反映で質問が消えた分だけ増える */
  resolved?: number;
  /** open のうち保留（空欄で記録）の数。answered とは別に数える */
  held: number;
  /** 直近に回答が記録された時刻 */
  lastAnsweredAt: Timestamp | null;
  /** 数え直した時刻（直近の再組版） */
  countedAt: Timestamp | null;
}

/** 実行中のジョブ。一覧に「生成中」を出すためだけの非正規化 */
export interface ActiveJob {
  type: JobType;
  status: JobStatus;
  startedAt: Timestamp | null;
}

/** 直近の失敗。一覧で「止まっている品目」を見つけるために出す */
export interface FailedJob {
  type: JobType;
  at: Timestamp | null;
  /**
   * 再実行では直らない失敗の種類(worker/lib/failure.mjs)。null・未設定は従来どおり「再実行」を案内する。
   * prep-doc-not-found = 選んだ文書に仕込みの作り方が書かれていなかった
   */
  kind?: FailureKind | null;
  /** 仕込みの取り込み・紐づけの対象(prep-doc / link-prep) */
  ingredient?: string | null;
}

/** 再実行では直らない失敗の種類。値は worker/lib/failure.mjs と揃える */
export type FailureKind = "prep-doc-not-found";

export type JobType =
  | "analyze"
  | "render"
  | "compare"
  | "resolve"
  | "approve"
  | "translate"
  | "link-prep"
  | "prep-doc";
export type JobStatus = "queued" | "running" | "succeeded" | "failed";

export interface JobDoc {
  type: JobType;
  status: JobStatus;
  startedAt: Timestamp | null;
  finishedAt: Timestamp | null;
  error: string | null;
  /**
   * 概算コスト(USD)。analyze のみ。
   *
   * **画面には出さない**(2026-08-30 柴田の指示)。運用側の記録として残しているだけで、
   * 実行履歴に出すと利用者に費用が見える。表示を足さないこと。
   */
  cost: number | null;
  runId: string | null;
  createdAt?: Timestamp | null;
  /** link-prep / prep-doc の対象の仕込み(#250) */
  ingredient?: string | null;
  /** prep-doc で取り込んだ文書(GCS のキー) */
  docPath?: string | null;
  /** 失敗の種類(FailedJob.kind と同じ)。種類を持たない失敗・古いジョブでは未設定 */
  errorKind?: FailureKind | null;
  /** 実行を指示した利用者。表示名は当時のものを焼き込んである（改名・退職に耐える） */
  requestedBy?: string | null;
  requestedByName?: string | null;
  requestedByEmail?: string | null;
  /** analyze の生成回数 */
  runs?: number | null;
  /** resolve / approve で反映した回答の件数 */
  answerCount?: number | null;
  /**
   * 承認を求めた実行(approve)で、承認が実際に適用されたか。
   * 未解消の確認事項が残っていると CLI が承認を拒む。ジョブ自体は成功で終わるので、
   * これが false のときは画面が理由を出す(押しても何も起きないように見せない)。
   */
  approveApplied?: boolean | null;
  /** 反映後に残った未解消の確認事項の件数 */
  remaining?: number | null;
}

/**
 * 操作の台帳（tenants/{t}/audit_logs）。
 * access_logs が「誰が何を見たか」なのに対し、こちらは「誰が何を変えたか」。
 * クライアントからは書けない（firestore.rules で write: false）。
 */
export type AuditAction =
  | "dish_materials_updated"
  | "dish_renamed"
  | "dish_deleted"
  | "dish_restored"
  | "evaluation_deleted"
  | "evaluation_restored"
  | "user_assigned"
  | "tasting_recorded";

/** 料理名の変更の前後（action: dish_renamed）。素材の before/after とは形が違う */
export interface DishName {
  name: string;
  nameJa: string | null;
}

export interface MaterialSet {
  videos: string[];
  docs: string[];
}

export interface MaterialChanges {
  addedVideos: string[];
  removedVideos: string[];
  reorderedVideos: boolean;
  addedDocs: string[];
  removedDocs: string[];
}

export interface AuditLogDoc {
  action: AuditAction | string;
  dishId?: string | null;
  dishName?: string | null;
  actorUid?: string | null;
  actorName?: string | null;
  actorEmail?: string | null;
  actorRole?: Role | null;
  reason?: string | null;
  before?: MaterialSet | null;
  after?: MaterialSet | null;
  changes?: MaterialChanges | null;
  /** 料理名の変更（dish_renamed）。素材の before/after と混ぜない */
  beforeName?: DishName | null;
  afterName?: DishName | null;
  /** 採点の重みの変更（score_weights_changed）。null は既定の重み */
  beforeWeights?: Record<string, number> | null;
  afterWeights?: Record<string, number> | null;
  createdAt: Timestamp | null;
}

/** 操作内容の表示。台帳に無い action は、そのままの値を出して隠さない */
export const AUDIT_ACTION_LABEL: Record<string, string> = {
  dish_materials_updated: "素材を差し替え",
  dish_renamed: "料理名を変更",
  dish_deleted: "品目を削除",
  dish_restored: "削除した品目を元に戻す",
  evaluation_deleted: "評価を削除",
  evaluation_restored: "削除した評価を元に戻す",
  user_assigned: "利用者の割り当て",
  user_invited: "利用者を招待",
  invite_canceled: "招待を取り消し",
  invite_accepted: "招待を受けて参加",
  user_updated: "利用者の設定を変更",
  user_dishes_changed: "担当品目を変更",
  user_removed: "利用者を削除",
  presence_started: "管理者の同席で閲覧を開始",
  presence_ended: "同席での閲覧を終了",
  tasting_recorded: "食味評価を記録",
  score_weights_changed: "採点の重みを変更",
};

/**
 * 親 evaluations/{evalId}.tasting(Functions トリガによる非正規化)。
 * 代表 = 最新 updatedAt の入力。工程スコア(score)とは別の数字で、合算しない。
 * 未評価(入力なし)は null。score: null は「入力はあるが点を出せなかった」状態。
 */
export interface TastingSummary {
  score: number | null;
  evaluatorName: string | null;
  at: Timestamp | null;
}

export const JOB_TYPE_LABEL: Record<JobType, string> = {
  analyze: "マニュアルを生成",
  render: "組版",
  compare: "比較",
  resolve: "回答を本文に反映",
  approve: "承認",
  translate: "中国語版を生成",
  "link-prep": "仕込みの紐づけ",
  "prep-doc": "仕込みの作り方をレシピから取り込み",
};

/** 管理者による招待（tenants/{t}/invites/{key}）。ログインした時点で消える */
export interface InviteDoc {
  email: string;
  role: Role;
  displayName: string | null;
  invitedBy: string;
  invitedByName: string | null;
  createdAt: Timestamp | null;
}

/** 同席中の記録（tenants/{t}/presence/{uid}__{dishId}）。functions/presence.mjs が書く */
export interface PresenceDoc {
  uid: string;
  workerName: string | null;
  dishId: string;
  dishName: string | null;
  adminUid: string;
  adminName: string | null;
  startedAt: Timestamp | null;
  lastSeenAt: Timestamp | null;
}

/** 同席が続いているとみなす時間。functions/presence-lib.mjs の PRESENCE_TTL_MS と同じ値 */
export const PRESENCE_TTL_MS = 90 * 1000;

export const ROLE_LABEL: Record<Role, string> = {
  admin: "管理者",
  chef: "料理長",
  supervisor: "上長",
  worker: "作業者",
};

export const JOB_STATUS_LABEL: Record<JobStatus, string> = {
  queued: "待機中",
  running: "実行中",
  succeeded: "完了",
  failed: "失敗",
};

export const STATUS_LABEL: Record<DishStatus, string> = {
  draft: "未生成",
  reviewing: "レビュー中",
  approved: "承認済み",
};
